/**
 * Interaction utilities for chart event handling.
 * Pure functions for coordinate conversion, zoom/pan calculations - no React dependencies.
 */

import { findNearestPointByTime } from "./hitTesting";
import type {
  ChartLayout,
  HoverEvent,
  OhlcvPoint,
  PricePoint,
  Scale,
  TickerDataPoint,
  TickerSeries,
} from "~types/types";

/** Zoom sensitivity: factor change per scroll tick (0.02 = 2%) */
export const ZOOM_SENSITIVITY = 0.02;

export interface ScreenToChartResult {
  chartX: number;
  chartY: number;
  timestamp: number;
}

export interface GestureType {
  isPinch: boolean;
  isHorizontalScroll: boolean;
  isMouseWheel: boolean;
}

export interface NearestPointsResult {
  nearestPoints: Map<string, TickerDataPoint>;
  timestamp: number;
}

/**
 * Convert screen event coordinates to chart coordinates with timestamp.
 * @param screenX - Client X coordinate from mouse event
 * @param screenY - Client Y coordinate from mouse event
 * @param rectLeft - Left edge of the SVG element bounding rect
 * @param rectTop - Top edge of the SVG element bounding rect
 * @param layout - Chart layout configuration
 * @param xScale - X-axis scale for timestamp conversion
 */
export function eventToChartCoords(
  screenX: number,
  screenY: number,
  rectLeft: number,
  rectTop: number,
  layout: ChartLayout,
  xScale: Scale
): ScreenToChartResult {
  const chartX = screenX - rectLeft + layout.priceArea.x;
  const chartY = screenY - rectTop + layout.priceArea.y;
  const timestamp = xScale.invert(chartX);

  return { chartX, chartY, timestamp };
}

/**
 * Convert screen X coordinate to chart X coordinate (simpler version for pan).
 */
export function screenXToChartX(
  screenX: number,
  rectLeft: number,
  layout: ChartLayout
): number {
  return screenX - rectLeft + layout.priceArea.x;
}

/**
 * Find nearest points for all series at a given X position.
 * Uses time-based snapping for better UX.
 */
export function findNearestPointsForSeries(
  seriesIds: string[],
  chartX: number,
  getVisibleData: (id: string) => (PricePoint | OhlcvPoint)[],
  xScale: Scale
): Map<string, TickerDataPoint> {
  const nearestPoints = new Map<string, TickerDataPoint>();

  for (const seriesId of seriesIds) {
    const data = getVisibleData(seriesId);
    const nearest = findNearestPointByTime(data, chartX, xScale);

    nearestPoints.set(seriesId, nearest?.point ?? null);
  }

  return nearestPoints;
}

/**
 * Calculate zoom factor from wheel deltaY.
 * Positive deltaY = scroll down = zoom out (factor < 1)
 * Negative deltaY = scroll up = zoom in (factor > 1)
 */
export function calculateZoomFactor(
  deltaY: number,
  sensitivity: number = ZOOM_SENSITIVITY
): number {
  return deltaY > 0 ? 1 - sensitivity : 1 + sensitivity;
}

/**
 * Threshold for horizontal scroll detection.
 * deltaX must be at least this many times larger than deltaY to be considered horizontal.
 * This prevents diagonal trackpad gestures from triggering both zoom and pan.
 */
const HORIZONTAL_DOMINANCE_THRESHOLD = 2;

/**
 * Detect gesture type from wheel event properties.
 * - ctrlKey is set by browser for pinch-to-zoom gestures (trackpad)
 * - Horizontal scroll requires deltaX to dominate deltaY (prevents diagonal conflicts)
 * - Pure vertical scroll (deltaY only, no ctrlKey) is mouse wheel → zoom
 */
export function detectGestureType(
  deltaX: number,
  deltaY: number,
  ctrlKey: boolean
): GestureType {
  const isPinch = ctrlKey;
  const absDeltaX = Math.abs(deltaX);
  const absDeltaY = Math.abs(deltaY);

  // Horizontal scroll only if deltaX clearly dominates deltaY
  // This prevents diagonal trackpad gestures from triggering both zoom and pan
  const isHorizontalScroll =
    absDeltaX > 0 && absDeltaX > absDeltaY * HORIZONTAL_DOMINANCE_THRESHOLD;

  // Mouse wheel: vertical scroll without pinch modifier and not horizontal dominant
  // Allow small horizontal drift (absDeltaX < absDeltaY * 0.5) for imprecise mouse wheels
  const isMouseWheel = absDeltaY > 0 && !isPinch && absDeltaX < absDeltaY * 0.5;

  return { isPinch, isHorizontalScroll, isMouseWheel };
}

/**
 * Determine if zoom should be applied based on gesture type.
 */
export function shouldZoom(gesture: GestureType): boolean {
  return gesture.isPinch || gesture.isMouseWheel;
}

/**
 * Determine if pan should be applied based on gesture type.
 */
export function shouldPan(gesture: GestureType): boolean {
  return gesture.isHorizontalScroll && !gesture.isPinch;
}

export type TouchGestureType = "none" | "pan" | "pinch";

/**
 * Detect touch gesture type based on number of active touches.
 * - 1 touch: pan gesture
 * - 2 touches: pinch/zoom gesture
 * - 0 or 3+ touches: no recognized gesture
 */
export function detectTouchGesture(touchCount: number): TouchGestureType {
  if (touchCount === 1) return "pan";
  if (touchCount === 2) return "pinch";

  return "none";
}

/**
 * Calculate the distance between two touch points.
 */
export function getTouchDistance(touch1: Touch, touch2: Touch): number {
  return Math.hypot(
    touch2.clientX - touch1.clientX,
    touch2.clientY - touch1.clientY
  );
}

/**
 * Calculate the center point between two touches.
 */
export function getTouchCenter(
  touch1: Touch,
  touch2: Touch
): { x: number; y: number } {
  return {
    x: (touch1.clientX + touch2.clientX) / 2,
    y: (touch1.clientY + touch2.clientY) / 2,
  };
}

/**
 * Build HoverEvent for single mode from interaction data.
 */
export function buildSingleModeHoverEvent(
  screenX: number,
  screenY: number,
  chartX: number,
  chartY: number,
  timestamp: number,
  series: TickerSeries,
  datum: TickerDataPoint
): HoverEvent {
  return {
    mode: "single",
    screenX,
    screenY,
    chartX,
    chartY,
    timestamp,
    series,
    datum,
  };
}

/**
 * Build HoverEvent for comparison mode from interaction data.
 */
export function buildComparisonModeHoverEvent(
  screenX: number,
  screenY: number,
  chartX: number,
  chartY: number,
  timestamp: number,
  nearestPoints: Map<string, TickerDataPoint>,
  seriesMetaMap: Map<string, TickerSeries>
): HoverEvent {
  const seriesPoints: Record<string, TickerDataPoint> = {};
  const seriesMeta: Record<string, TickerSeries> = {};

  for (const [id, point] of nearestPoints) {
    seriesPoints[id] = point;
    const meta = seriesMetaMap.get(id);

    if (meta) seriesMeta[id] = meta;
  }

  return {
    mode: "comparison",
    screenX,
    screenY,
    chartX,
    chartY,
    timestamp,
    seriesPoints,
    seriesMeta,
  };
}

export interface BuildInteractionHoverEventOptions {
  readonly mode: "single" | "comparison";
  readonly screenX: number;
  readonly screenY: number;
  readonly chartX: number;
  readonly chartY: number;
  readonly timestamp: number;
  readonly seriesIds: string[];
  readonly nearestPoints: Map<string, TickerDataPoint>;
  readonly seriesMetaMap: Map<string, TickerSeries>;
}

/**
 * Build appropriate HoverEvent based on chart mode.
 */
export function buildInteractionHoverEvent(
  options: BuildInteractionHoverEventOptions
): HoverEvent | null {
  const {
    mode,
    screenX,
    screenY,
    chartX,
    chartY,
    timestamp,
    seriesIds,
    nearestPoints,
    seriesMetaMap,
  } = options;

  if (mode === "single") {
    const seriesId = seriesIds[0];

    if (!seriesId) return null;

    const series = seriesMetaMap.get(seriesId);

    if (!series) return null;

    const datum = nearestPoints.get(seriesId) ?? null;

    return buildSingleModeHoverEvent(
      screenX,
      screenY,
      chartX,
      chartY,
      timestamp,
      series,
      datum
    );
  }

  return buildComparisonModeHoverEvent(
    screenX,
    screenY,
    chartX,
    chartY,
    timestamp,
    nearestPoints,
    seriesMetaMap
  );
}
